import React, { useState, useMemo } from "react";
import { ChevronsUpDown, ChevronLeft, ChevronRight, Check, Search, X } from "lucide-react";

// Mock FormatDateTime function
const FormatDateTime = (dateString) => {
  return dateString;
};

// UPDATED: Dynamic lot size configuration (removed NIFTY hardcoded value)
const LOT_SIZE_CONFIG = {
  BANKNIFTY: 30 // Keep BANKNIFTY fixed, NIFTY will be calculated dynamically
};

// UPDATED: Helper function to get lot size based on symbol for BANKNIFTY only
const getLotSize = (symbol) => {
  const upperSymbol = symbol.toUpperCase();
  
  if (upperSymbol.includes('BANKNIFTY')) {
    // BANKNIFTY: Keep ORIGINAL logic - fixed lot size, no changes
    return LOT_SIZE_CONFIG.BANKNIFTY;
  } else if (upperSymbol.includes('NIFTY')) {
    // NIFTY: Will be calculated using CA00246 reference
    return null;
  }
  
  return null; // Stock option
};

// NEW: Helper function to get base quantity for NIFTY using CA00246 reference
const getNiftyTradeBaseQuantity = (symbol, strategy, side, tradeData = [], accountNames = []) => {
  const referenceClientId = "CA00246";
  
  console.log(`\n=== DEBUGGING NIFTY TRADE BASE CALCULATION ===`);
  console.log(`🎯 Target: Symbol="${symbol}", Strategy="${strategy}", Side=${side}, Client="${referenceClientId}"`);
  
  // Find reference client's multiplier from accountNames
  // console.log(`\n📋 Available Accounts:`);
  accountNames.forEach(u => console.log(`  - ID: "${u.client_id}", Name: "${u.name}", Multiplier: ${u.index_multiplier}`));
  
  const referenceClientData = accountNames.find(user => user.client_id === referenceClientId);
  if (!referenceClientData) {
    console.log(`❌ Reference client "${referenceClientId}" NOT FOUND in accountNames`);
    console.log(`📋 Available client IDs: [${accountNames.map(u => `"${u.client_id}"`).join(', ')}]`);
    return 75; // Fallback
  }
  
  console.log(`✅ Found reference client: ID="${referenceClientData.client_id}", Multiplier=${referenceClientData.index_multiplier}`);
  const referenceMultiplier = referenceClientData.index_multiplier;
  
  // Show ALL trades for this symbol
  console.log(`\n📦 All trades for symbol "${symbol}":`);
  const symbolTrades = tradeData.filter(t => t.symbol === symbol);
  symbolTrades.forEach((t, i) => {
    console.log(`  [${i}] Strategy="${t.strategy}", Side=${t.side}, TradedQty=${t.tradedqty}, ClientIDs=[${t.clientIds.join(', ')}]`);
  });
  
  // Find reference client's trade quantity for this EXACT symbol + strategy + side combination
  console.log(`\n🔍 Looking for EXACT match: Symbol="${symbol}" + Strategy="${strategy}" + Side=${side} + Client="${referenceClientId}"`);
  
  const referenceTrade = tradeData.find(trade => 
    trade.symbol === symbol && 
    trade.strategy === strategy && 
    trade.side === side && 
    trade.clientIds.includes(referenceClientId)
  );
  
  if (!referenceTrade) {
    console.log(`❌ NO EXACT MATCH FOUND!`);
    console.log(`📦 Trades matching symbol+strategy+side (without client filter):`);
    const partialMatches = tradeData.filter(t => 
      t.symbol === symbol && t.strategy === strategy && t.side === side
    );
    partialMatches.forEach(t => {
      console.log(`  - TradedQty=${t.tradedqty}, ClientIDs=[${t.clientIds.join(', ')}], Contains "${referenceClientId}"? ${t.clientIds.includes(referenceClientId)}`);
    });
    console.log(`🔄 FALLING BACK TO 75`);
    return 75; // Fallback
  }
  
  console.log(`✅ FOUND EXACT MATCH!`);
  console.log(`📦 Trade details: Strategy="${referenceTrade.strategy}", Side=${referenceTrade.side}, TradedQty=${referenceTrade.tradedqty}`);
  console.log(`👥 ClientIDs in this trade: [${referenceTrade.clientIds.join(', ')}]`);
  
  const referenceQty = referenceTrade.tradedqty;
  
  // Calculate base quantity
  const baseQty = referenceQty / referenceMultiplier;
  
  console.log(`\n📊 CALCULATION:`);
  console.log(`   TradedQty: ${referenceQty}`);
  console.log(`   Multiplier: ${referenceMultiplier}`);
  console.log(`   Base: ${referenceQty} ÷ ${referenceMultiplier} = ${baseQty}`);
  console.log(`🎉 FINAL BASE QUANTITY: ${baseQty}`);
  console.log(`=== END DEBUGGING ===\n`);
  
  return baseQty;
};

// NEW: Helper function to calculate expected quantity for NIFTY trades
const calculateNiftyTradeExpectedQuantity = (baseQty, targetMultiplier) => {
  // Calculate expected quantity
  const expectedQty = baseQty * targetMultiplier;
  
  // Round to nearest multiple of 75 if needed
  const roundedQty = Math.round(expectedQty / 75) * 75;
  
  console.log(`NIFTY trade calculation: base=${baseQty} * multiplier=${targetMultiplier} = ${expectedQty} → rounded to ${roundedQty}`);
  
  return roundedQty;
};

// Helper function to check if symbol is index option
const isIndexOption = (symbol) => {
  const upperSymbol = symbol.toUpperCase();
  return upperSymbol.includes('NIFTY') || upperSymbol.includes('BANKNIFTY');
};

export default function NewLiveTradesTable({
  liveTradeData,
  filteredLiveTradesData = [],
  setFilteredLiveTradesFno,
  setFilteredLiveTradesEquity,
  sortOrder,
  setSortOrder,
  currentState,
  accountName = [], // This is the accountNames prop
  allUser = [], // Keeping this for backward compatibility
  ...props
}) {

  // Use accountName as the primary source, fallback to allUser for backward compatibility
  const accountNames = accountName.length > 0 ? accountName : allUser;
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');

  console.log("AccountNames data:", accountNames);
  
  // UPDATED: Symbol detection with CA00246 reference logic for NIFTY only
  console.log("Symbol detection test:", filteredLiveTradesData.map(trade => ({
    symbol: trade.symbol,
    strategy: trade.strategy,
    side: trade.side,
    isIndex: isIndexOption(trade.symbol),
    lotSize: getLotSize(trade.symbol), // Original logic for BANKNIFTY
    niftyBaseQty: trade.symbol.toUpperCase().includes('NIFTY') && !trade.symbol.toUpperCase().includes('BANKNIFTY') 
      ? `Will calculate for strategy ${trade.strategy}, side ${trade.side}` 
      : null,
    tradedqty: trade.tradedqty
  })));
  
  // Group trades by symbol + strategy + side
  const groupedTrades = useMemo(() => {
    const groups = {};
    
    filteredLiveTradesData.forEach(trade => {
      const key = `${trade.symbol}-${trade.strategy}-${trade.side}`;
      
      if (!groups[key]) {
        groups[key] = {
          symbol: trade.symbol,
          strategy: trade.strategy,
          side: trade.side,
          orderdatetime: trade.orderdatetime,
          tradeprice: trade.tradeprice,
          actualQuantities: {}, // Store actual quantities from trades
          clientTradeMap: {} // Map client to their actual quantities
        };
      }
      
      // Store actual quantities and client mappings
      trade.clientIds.forEach(clientId => {
        if (!groups[key].clientTradeMap[clientId]) {
          groups[key].clientTradeMap[clientId] = [];
        }
        groups[key].clientTradeMap[clientId].push(trade.tradedqty);
      });
      
      if (!groups[key].actualQuantities[trade.tradedqty]) {
        groups[key].actualQuantities[trade.tradedqty] = [];
      }
      groups[key].actualQuantities[trade.tradedqty].push(...trade.clientIds);
    });
    
    // Remove duplicates from clientTradeMap
    Object.values(groups).forEach(group => {
      Object.keys(group.clientTradeMap).forEach(clientId => {
        group.clientTradeMap[clientId] = [...new Set(group.clientTradeMap[clientId])];
      });
    });
    
    return Object.values(groups);
  }, [filteredLiveTradesData]);

  // UPDATED: Generate multiplier tiers - ORIGINAL logic for BANKNIFTY, CA00246 logic for NIFTY only
  const enhancedGroupedTrades = useMemo(() => {
    return groupedTrades.map(group => {
      const isIndex = isIndexOption(group.symbol);
      
      if (isIndex) {
        if (group.symbol.toUpperCase().includes('BANKNIFTY')) {
          // BANKNIFTY: Keep EXACT ORIGINAL logic - Force multiplier 1, show all clients
          const lotSize = LOT_SIZE_CONFIG.BANKNIFTY;
          
          console.log(`Processing BANKNIFTY trade ${group.symbol} with fixed lot size: ${lotSize}`);
          
          // Force multiplier 1 for BANKNIFTY (original logic)
          const uniqueMultipliers = [1];
          
          const multiplierTiers = uniqueMultipliers.map(multiplier => {
            const calculatedQty = multiplier * lotSize; // 1 * 30 = 30
            
            // Get clients who actually have this quantity
            const clientsWithThisQty = group.actualQuantities[calculatedQty] || [];
            
            return {
              multiplier,
              quantity: calculatedQty,
              // Show ALL clients for BANKNIFTY (original logic)
              allClients: accountNames,
              clientsWithTrade: clientsWithThisQty
            };
          });
          
          return {
            ...group,
            multiplierTiers,
            isIndexOption: true,
            baseQuantity: lotSize // Store for reference
          };
          
        } else if (group.symbol.toUpperCase().includes('NIFTY')) {
          // NIFTY ONLY: Use CA00246 reference logic with exact symbol+strategy+side match
          const baseQty = getNiftyTradeBaseQuantity(group.symbol, group.strategy, group.side, filteredLiveTradesData, accountNames);
          
          console.log(`Processing NIFTY trade ${group.symbol} (${group.strategy}, side ${group.side}) with CA00246 base quantity: ${baseQty}`);
          
          // Get unique multipliers from accountNames
          const uniqueMultipliers = [...new Set(
            accountNames
              .map(user => user.index_multiplier)
              .filter(multiplier => multiplier > 0)
          )].sort((a, b) => a - b);
          
          const multiplierTiers = uniqueMultipliers.map(multiplier => {
            // Calculate expected quantity using NIFTY logic
            const expectedQty = calculateNiftyTradeExpectedQuantity(baseQty, multiplier);
            
            // Get clients who actually have this quantity (or closest to it)
            const clientsWithThisQty = group.actualQuantities[expectedQty] || [];
            
            // For NIFTY: Also check for clients with quantities close to expected (within 10% tolerance for rounding)
            const tolerance = expectedQty * 0.1;
            const clientsWithSimilarQty = [];
            
            Object.entries(group.actualQuantities).forEach(([qty, clientIds]) => {
              const actualQty = parseInt(qty);
              if (Math.abs(actualQty - expectedQty) <= tolerance && actualQty !== expectedQty) {
                clientsWithSimilarQty.push(...clientIds);
              }
            });
            
            const allClientsWithTrade = [...new Set([...clientsWithThisQty, ...clientsWithSimilarQty])];
            
            return {
              multiplier,
              quantity: expectedQty,
              expectedQuantity: expectedQty, // Store expected for reference
              allClients: accountNames.filter(user => user.index_multiplier === multiplier),
              clientsWithTrade: allClientsWithTrade
            };
          });
          
          return {
            ...group,
            multiplierTiers,
            isIndexOption: true,
            baseQuantity: baseQty // Store for reference
          };
        }
      } else {
        // For stock options, keep current logic
        const quantityTiers = Object.entries(group.actualQuantities)
          .map(([qty, clientIds]) => ({
            quantity: parseInt(qty),
            clientIds: [...new Set(clientIds)]
          }))
          .sort((a, b) => a.quantity - b.quantity);
          
        return {
          ...group,
          quantityTiers,
          isIndexOption: false
        };
      }
    });
  }, [groupedTrades, accountNames, filteredLiveTradesData]);

  console.log("Enhanced grouped trades (ORIGINAL logic for BANKNIFTY, CA00246 logic for NIFTY only):", enhancedGroupedTrades);

  // Search and sorting logic (Type 2 search - reorder, don't filter)
 

  const searchedAndSortedTrades = useMemo(() => {
      let result = [...enhancedGroupedTrades];
      
      // Apply sorting first if configured
      if (sortConfig.key) {
        result.sort((a, b) => {
          let aValue = a[sortConfig.key];
          let bValue = b[sortConfig.key];
          
          // Handle different data types
          if (sortConfig.key === 'symbol') {
            aValue = aValue.replace("NSE:", "").toLowerCase();
            bValue = bValue.replace("NSE:", "").toLowerCase();
          } else if (sortConfig.key === 'side') {
            aValue = aValue === 1 ? 'buy' : 'sell';
            bValue = bValue === 1 ? 'buy' : 'sell';
          } else if (sortConfig.key === 'netQty' || sortConfig.key === 'netAvg' || sortConfig.key === 'ltp' || sortConfig.key === 'pl') {
            aValue = parseFloat(aValue) || 0;
            bValue = parseFloat(bValue) || 0;
          }
          
          if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
          if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        });
      }
      
      // Then apply search reordering (matching items go to top while maintaining sort order within each group)
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase().trim();
        
        // Separate matching and non-matching positions while preserving their sorted order
        const matchingPositions = result.filter(position => 
          position.symbol.toLowerCase().includes(searchLower)
        );
        const nonMatchingPositions = result.filter(position => 
          !position.symbol.toLowerCase().includes(searchLower)
        );
        
        // Combine: matching positions first (maintaining their sort order), then non-matching (maintaining their sort order)
        result = [...matchingPositions, ...nonMatchingPositions];
      }
      
      return result;
    }, [enhancedGroupedTrades, searchTerm, sortConfig]);
  

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const getSideColor = (side) => {
    return side === 1 ? "text-green-400" : "text-red-400";
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronsUpDown className="w-4 h-4" />;
    }
    return sortConfig.direction === 'asc' ? 
      <ChevronsUpDown className="w-4 h-4 text-blue-400" /> : 
      <ChevronsUpDown className="w-4 h-4 text-blue-400 rotate-180" />;
  };

  if (!filteredLiveTradesData || filteredLiveTradesData.length === 0) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-zinc-400">
        <div className="text-lg font-medium">No Trade History Found!!</div>
      </div>
    );
  }

  return (
    <div className="w-full h-[calc(100vh-60px)] bg-zinc-900 flex flex-col">
      {/* Search Bar */}
      <div className="bg-zinc-800 border-b border-zinc-700 p-4 shrink-0">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by symbol..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-10 py-1 bg-zinc-700 border border-zinc-600 rounded-lg text-zinc-200 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {/* {searchTerm && (
          <div className="mt-2 text-sm text-zinc-400">
            Showing results for "{searchTerm}" - matching trades appear at the top
          </div>
        )} */}
      </div>

      {/* Sticky Header */}
      <div className="bg-zinc-800 border-b border-zinc-700 sticky top-0 z-10 shrink-0">
        <div className="grid grid-cols-6 gap-4 px-6 py-2 text-sm font-medium text-zinc-300">
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('symbol')}
          >
            <span>Symbol</span>
            {getSortIcon('symbol')}
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('orderdatetime')}
          >
            <span>Date</span>
            {getSortIcon('orderdatetime')}
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('side')}
          >
            <span>Side</span>
            {getSortIcon('side')}
          </button>

          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('quantity')}
          >
            <span>Quantity</span>
            {getSortIcon('quantity')}
          </button>

          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('tradeprice')}
          >
            <span>Traded Price</span>
            {getSortIcon('tradeprice')}
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('strategy')}
          >
            <span>Strategy</span>
            {getSortIcon('strategy')}
          </button>
        </div>
      </div>

      {/* Scrollable Table Body - No Pagination, All Results Shown */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="min-w-full bg-zinc-900">
          <div className="divide-y divide-zinc-700">
            {searchedAndSortedTrades.map((trade, index) => (
              <div key={index} className={` ${
                searchTerm && trade.symbol.toLowerCase().includes(searchTerm.toLowerCase()) 
                  ? ' bg-[#07F2C7]/50' 
                  : 'bg-zinc-900'
              }`}>
                {/* Main Trade Row */}
                <div className="grid grid-cols-6 gap-4 px-6 py-2 text-sm text-zinc-200 hover:bg-zinc-800 transition-colors">
                  <div className="font-medium">
                    {trade.symbol.replace("NSE:", "")}
                  </div>
                  <div>{FormatDateTime(trade.orderdatetime)}</div>
                  <div className={`font-medium ${getSideColor(trade.side)}`}>
                    {trade.side === 1 ? "BUY" : "SELL"}
                  </div>
                  <div>{trade.isIndexOption ? 
                    trade.multiplierTiers?.reduce((sum, tier) => sum + (tier.clientsWithTrade.length * tier.quantity), 0) :
                    trade.quantityTiers?.reduce((sum, tier) => sum + tier.quantity, 0)
                  }</div>
                  <div>₹{trade.tradeprice}</div>
                  <div className="font-medium">{trade.strategy}</div>
                </div>

                {/* Enhanced Quantity-Client Sub Table */}
                <div className="px-6 pb-4">
                  <div className="bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700">
                    {trade.isIndexOption ? (
                      // Index Options - ORIGINAL logic for BANKNIFTY, CA00246 logic for NIFTY
                      <>
                        <div className="bg-zinc-700 px-4 py-2">
                          <div className="flex gap-4 text-xs font-medium text-zinc-300">
                            <div className="min-w-[125px] flex items-start justify-start">Index Multiplier</div>
                            <div className="min-w-[125px] flex items-start justify-start">
                              {trade.symbol.toUpperCase().includes('BANKNIFTY') ? 'Quantity' : 'Expected Qty'}
                            </div>
                            <div className="min-w-[140px] flex items-start justify-start">Clients</div>
                            {trade.baseQuantity && (
                              <div className="min-w-[120px] flex items-start justify-start text-amber-400">
                                {trade.symbol.toUpperCase().includes('BANKNIFTY') 
                                  ? `Base: ${trade.baseQuantity} (Fixed)`
                                  : `Base: ${trade.baseQuantity} (CA00246)`
                                }
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="divide-y divide-zinc-700">
                          {trade.multiplierTiers.map((tier, tierIndex) => (
                            <div key={tierIndex} className="grid grid-cols-[120px,120px,1fr] gap-4 px-4 py-3 text-sm items-start">
                              <div className="font-medium text-blue-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
                                {tier.multiplier}
                              </div>
                              <div className="font-medium text-emerald-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
                                {tier.quantity}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {tier.allClients.map((user, userIndex) => {
                                  const hasTrade = tier.clientsWithTrade.includes(user.client_id);
                                  return (
                                    <span 
                                      key={userIndex}
                                      className={`text-zinc-300 rounded px-2 py-1 text-xs flex items-center space-x-1 ${
                                        hasTrade ? 'bg-green-800 border border-green-500/30' : 'bg-zinc-700'
                                      }`}
                                    >
                                      {hasTrade && (
                                        <Check className="w-3 h-3 text-white" />
                                      )}
                                      <span>{user.name}</span>
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      // Stock Options - Current Logic
                      <>
                        <div className="bg-zinc-700 px-4 py-2">
                          <div className="grid grid-cols-2 gap-4 text-xs font-medium text-zinc-300">
                            <div>Quantity</div>
                            <div>Clients</div>
                          </div>
                        </div>
                        
                        <div className="divide-y divide-zinc-700">
                          {trade.quantityTiers.map((tier, tierIndex) => (
                            <div key={tierIndex} className="grid grid-cols-[120px,1fr] gap-4 px-4 py-3 text-sm items-start">
                              <div className="font-medium text-blue-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
                                {tier.quantity}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {accountNames
                                  .filter(user => tier.clientIds.includes(user.client_id))
                                  .map((user, userIndex) => {
                                    const hasTrade = tier.clientIds.includes(user.client_id);
                                    return (
                                      <span 
                                        key={userIndex}
                                        className={`text-zinc-300 rounded px-2 py-1 text-xs flex items-center space-x-1 ${
                                          hasTrade ? 'bg-green-900 border border-green-500/30' : 'bg-zinc-700'
                                        }`}
                                      >
                                        {hasTrade && (
                                          <Check className="w-3 h-3 text-white" />
                                        )}
                                        <span>{user.name}</span>
                                      </span>
                                    );
                                  })
                                }
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      
    </div>
  );
}


























































































































// import React, { useState, useMemo } from "react";
// import { ChevronsUpDown, ChevronLeft, ChevronRight, Check } from "lucide-react";

// // Mock FormatDateTime function
// const FormatDateTime = (dateString) => {
//   return dateString;
// };

// // UPDATED: Dynamic lot size configuration (removed NIFTY hardcoded value)
// const LOT_SIZE_CONFIG = {
//   BANKNIFTY: 30 // Keep BANKNIFTY fixed, NIFTY will be calculated dynamically
// };

// // UPDATED: Helper function to get lot size based on symbol for BANKNIFTY only
// const getLotSize = (symbol) => {
//   const upperSymbol = symbol.toUpperCase();
  
//   if (upperSymbol.includes('BANKNIFTY')) {
//     // BANKNIFTY: Keep ORIGINAL logic - fixed lot size, no changes
//     return LOT_SIZE_CONFIG.BANKNIFTY;
//   } else if (upperSymbol.includes('NIFTY')) {
//     // NIFTY: Will be calculated using CA00246 reference
//     return null;
//   }
  
//   return null; // Stock option
// };

// // NEW: Helper function to get base quantity for NIFTY using CA00246 reference
// const getNiftyTradeBaseQuantity = (symbol, strategy, side, tradeData = [], accountNames = []) => {
//   const referenceClientId = "CA00246";
  
//   console.log(`\n=== DEBUGGING NIFTY TRADE BASE CALCULATION ===`);
//   console.log(`🎯 Target: Symbol="${symbol}", Strategy="${strategy}", Side=${side}, Client="${referenceClientId}"`);
  
//   // Find reference client's multiplier from accountNames
//   console.log(`\n📋 Available Accounts:`);
//   accountNames.forEach(u => console.log(`  - ID: "${u.client_id}", Name: "${u.name}", Multiplier: ${u.index_multiplier}`));
  
//   const referenceClientData = accountNames.find(user => user.client_id === referenceClientId);
//   if (!referenceClientData) {
//     console.log(`❌ Reference client "${referenceClientId}" NOT FOUND in accountNames`);
//     console.log(`📋 Available client IDs: [${accountNames.map(u => `"${u.client_id}"`).join(', ')}]`);
//     return 75; // Fallback
//   }
  
//   console.log(`✅ Found reference client: ID="${referenceClientData.client_id}", Multiplier=${referenceClientData.index_multiplier}`);
//   const referenceMultiplier = referenceClientData.index_multiplier;
  
//   // Show ALL trades for this symbol
//   console.log(`\n📦 All trades for symbol "${symbol}":`);
//   const symbolTrades = tradeData.filter(t => t.symbol === symbol);
//   symbolTrades.forEach((t, i) => {
//     console.log(`  [${i}] Strategy="${t.strategy}", Side=${t.side}, TradedQty=${t.tradedqty}, ClientIDs=[${t.clientIds.join(', ')}]`);
//   });
  
//   // Find reference client's trade quantity for this EXACT symbol + strategy + side combination
//   console.log(`\n🔍 Looking for EXACT match: Symbol="${symbol}" + Strategy="${strategy}" + Side=${side} + Client="${referenceClientId}"`);
  
//   const referenceTrade = tradeData.find(trade => 
//     trade.symbol === symbol && 
//     trade.strategy === strategy && 
//     trade.side === side && 
//     trade.clientIds.includes(referenceClientId)
//   );
  
//   if (!referenceTrade) {
//     console.log(`❌ NO EXACT MATCH FOUND!`);
//     console.log(`📦 Trades matching symbol+strategy+side (without client filter):`);
//     const partialMatches = tradeData.filter(t => 
//       t.symbol === symbol && t.strategy === strategy && t.side === side
//     );
//     partialMatches.forEach(t => {
//       console.log(`  - TradedQty=${t.tradedqty}, ClientIDs=[${t.clientIds.join(', ')}], Contains "${referenceClientId}"? ${t.clientIds.includes(referenceClientId)}`);
//     });
//     console.log(`🔄 FALLING BACK TO 75`);
//     return 75; // Fallback
//   }
  
//   console.log(`✅ FOUND EXACT MATCH!`);
//   console.log(`📦 Trade details: Strategy="${referenceTrade.strategy}", Side=${referenceTrade.side}, TradedQty=${referenceTrade.tradedqty}`);
//   console.log(`👥 ClientIDs in this trade: [${referenceTrade.clientIds.join(', ')}]`);
  
//   const referenceQty = referenceTrade.tradedqty;
  
//   // Calculate base quantity
//   const baseQty = referenceQty / referenceMultiplier;
  
//   console.log(`\n📊 CALCULATION:`);
//   console.log(`   TradedQty: ${referenceQty}`);
//   console.log(`   Multiplier: ${referenceMultiplier}`);
//   console.log(`   Base: ${referenceQty} ÷ ${referenceMultiplier} = ${baseQty}`);
//   console.log(`🎉 FINAL BASE QUANTITY: ${baseQty}`);
//   console.log(`=== END DEBUGGING ===\n`);
  
//   return baseQty;
// };

// // NEW: Helper function to calculate expected quantity for NIFTY trades
// const calculateNiftyTradeExpectedQuantity = (baseQty, targetMultiplier) => {
//   // Calculate expected quantity
//   const expectedQty = baseQty * targetMultiplier;
  
//   // Round to nearest multiple of 75 if needed
//   const roundedQty = Math.round(expectedQty / 75) * 75;
  
//   console.log(`NIFTY trade calculation: base=${baseQty} * multiplier=${targetMultiplier} = ${expectedQty} → rounded to ${roundedQty}`);
  
//   return roundedQty;
// };

// // Helper function to check if symbol is index option
// const isIndexOption = (symbol) => {
//   const upperSymbol = symbol.toUpperCase();
//   return upperSymbol.includes('NIFTY') || upperSymbol.includes('BANKNIFTY');
// };

// export default function NewLiveTradesTable({
//   liveTradeData,
//   filteredLiveTradesData = [],
//   setFilteredLiveTradesFno,
//   setFilteredLiveTradesEquity,
//   sortOrder,
//   setSortOrder,
//   currentState,
//   accountName = [], // This is the accountNames prop
//   allUser = [], // Keeping this for backward compatibility
//   currentPage,
//   setCurrentPage,
//   ...props
// }) {

//   // Use accountName as the primary source, fallback to allUser for backward compatibility
//   const accountNames = accountName.length > 0 ? accountName : allUser;
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

//   console.log("AccountNames data:", accountNames);
  
//   // UPDATED: Symbol detection with CA00246 reference logic for NIFTY only
//   console.log("Symbol detection test:", filteredLiveTradesData.map(trade => ({
//     symbol: trade.symbol,
//     strategy: trade.strategy,
//     side: trade.side,
//     isIndex: isIndexOption(trade.symbol),
//     lotSize: getLotSize(trade.symbol), // Original logic for BANKNIFTY
//     niftyBaseQty: trade.symbol.toUpperCase().includes('NIFTY') && !trade.symbol.toUpperCase().includes('BANKNIFTY') 
//       ? `Will calculate for strategy ${trade.strategy}, side ${trade.side}` 
//       : null,
//     tradedqty: trade.tradedqty
//   })));
  
//   // Group trades by symbol + strategy + side
//   const groupedTrades = useMemo(() => {
//     const groups = {};
    
//     filteredLiveTradesData.forEach(trade => {
//       const key = `${trade.symbol}-${trade.strategy}-${trade.side}`;
      
//       if (!groups[key]) {
//         groups[key] = {
//           symbol: trade.symbol,
//           strategy: trade.strategy,
//           side: trade.side,
//           orderdatetime: trade.orderdatetime,
//           tradeprice: trade.tradeprice,
//           actualQuantities: {}, // Store actual quantities from trades
//           clientTradeMap: {} // Map client to their actual quantities
//         };
//       }
      
//       // Store actual quantities and client mappings
//       trade.clientIds.forEach(clientId => {
//         if (!groups[key].clientTradeMap[clientId]) {
//           groups[key].clientTradeMap[clientId] = [];
//         }
//         groups[key].clientTradeMap[clientId].push(trade.tradedqty);
//       });
      
//       if (!groups[key].actualQuantities[trade.tradedqty]) {
//         groups[key].actualQuantities[trade.tradedqty] = [];
//       }
//       groups[key].actualQuantities[trade.tradedqty].push(...trade.clientIds);
//     });
    
//     // Remove duplicates from clientTradeMap
//     Object.values(groups).forEach(group => {
//       Object.keys(group.clientTradeMap).forEach(clientId => {
//         group.clientTradeMap[clientId] = [...new Set(group.clientTradeMap[clientId])];
//       });
//     });
    
//     return Object.values(groups);
//   }, [filteredLiveTradesData]);

//   // UPDATED: Generate multiplier tiers - ORIGINAL logic for BANKNIFTY, CA00246 logic for NIFTY only
//   const enhancedGroupedTrades = useMemo(() => {
//     return groupedTrades.map(group => {
//       const isIndex = isIndexOption(group.symbol);
      
//       if (isIndex) {
//         if (group.symbol.toUpperCase().includes('BANKNIFTY')) {
//           // BANKNIFTY: Keep EXACT ORIGINAL logic - Force multiplier 1, show all clients
//           const lotSize = LOT_SIZE_CONFIG.BANKNIFTY;
          
//           console.log(`Processing BANKNIFTY trade ${group.symbol} with fixed lot size: ${lotSize}`);
          
//           // Force multiplier 1 for BANKNIFTY (original logic)
//           const uniqueMultipliers = [1];
          
//           const multiplierTiers = uniqueMultipliers.map(multiplier => {
//             const calculatedQty = multiplier * lotSize; // 1 * 30 = 30
            
//             // Get clients who actually have this quantity
//             const clientsWithThisQty = group.actualQuantities[calculatedQty] || [];
            
//             return {
//               multiplier,
//               quantity: calculatedQty,
//               // Show ALL clients for BANKNIFTY (original logic)
//               allClients: accountNames,
//               clientsWithTrade: clientsWithThisQty
//             };
//           });
          
//           return {
//             ...group,
//             multiplierTiers,
//             isIndexOption: true,
//             baseQuantity: lotSize // Store for reference
//           };
          
//         } else if (group.symbol.toUpperCase().includes('NIFTY')) {
//           // NIFTY ONLY: Use CA00246 reference logic with exact symbol+strategy+side match
//           const baseQty = getNiftyTradeBaseQuantity(group.symbol, group.strategy, group.side, filteredLiveTradesData, accountNames);
          
//           console.log(`Processing NIFTY trade ${group.symbol} (${group.strategy}, side ${group.side}) with CA00246 base quantity: ${baseQty}`);
          
//           // Get unique multipliers from accountNames
//           const uniqueMultipliers = [...new Set(
//             accountNames
//               .map(user => user.index_multiplier)
//               .filter(multiplier => multiplier > 0)
//           )].sort((a, b) => a - b);
          
//           const multiplierTiers = uniqueMultipliers.map(multiplier => {
//             // Calculate expected quantity using NIFTY logic
//             const expectedQty = calculateNiftyTradeExpectedQuantity(baseQty, multiplier);
            
//             // Get clients who actually have this quantity (or closest to it)
//             const clientsWithThisQty = group.actualQuantities[expectedQty] || [];
            
//             // For NIFTY: Also check for clients with quantities close to expected (within 10% tolerance for rounding)
//             const tolerance = expectedQty * 0.1;
//             const clientsWithSimilarQty = [];
            
//             Object.entries(group.actualQuantities).forEach(([qty, clientIds]) => {
//               const actualQty = parseInt(qty);
//               if (Math.abs(actualQty - expectedQty) <= tolerance && actualQty !== expectedQty) {
//                 clientsWithSimilarQty.push(...clientIds);
//               }
//             });
            
//             const allClientsWithTrade = [...new Set([...clientsWithThisQty, ...clientsWithSimilarQty])];
            
//             return {
//               multiplier,
//               quantity: expectedQty,
//               expectedQuantity: expectedQty, // Store expected for reference
//               allClients: accountNames.filter(user => user.index_multiplier === multiplier),
//               clientsWithTrade: allClientsWithTrade
//             };
//           });
          
//           return {
//             ...group,
//             multiplierTiers,
//             isIndexOption: true,
//             baseQuantity: baseQty // Store for reference
//           };
//         }
//       } else {
//         // For stock options, keep current logic
//         const quantityTiers = Object.entries(group.actualQuantities)
//           .map(([qty, clientIds]) => ({
//             quantity: parseInt(qty),
//             clientIds: [...new Set(clientIds)]
//           }))
//           .sort((a, b) => a.quantity - b.quantity);
          
//         return {
//           ...group,
//           quantityTiers,
//           isIndexOption: false
//         };
//       }
//     });
//   }, [groupedTrades, accountNames, filteredLiveTradesData]);

//   console.log("Enhanced grouped trades (ORIGINAL logic for BANKNIFTY, CA00246 logic for NIFTY only):", enhancedGroupedTrades);

//   // Sorting logic
//   const sortedTrades = useMemo(() => {
//     if (!sortConfig.key) return enhancedGroupedTrades;
    
//     return [...enhancedGroupedTrades].sort((a, b) => {
//       let aValue = a[sortConfig.key];
//       let bValue = b[sortConfig.key];
      
//       if (sortConfig.key === 'symbol') {
//         aValue = aValue.replace("NSE:", "");
//         bValue = bValue.replace("NSE:", "");
//       }
      
//       if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
//       if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
//       return 0;
//     });
//   }, [enhancedGroupedTrades, sortConfig]);

//   // Pagination
//   const itemsPerPage = 10;
//   const indexOfLastItem = currentPage * itemsPerPage;
//   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//   const currentItems = sortedTrades.slice(indexOfFirstItem, indexOfLastItem);
//   const totalPages = Math.ceil(sortedTrades.length / itemsPerPage);

//   const handleSort = (key) => {
//     setSortConfig(prev => ({
//       key,
//       direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
//     }));
//   };

//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//   };

//   const getPageNumbers = () => {
//     const pageNumbers = [];
//     const maxVisible = 5;
    
//     let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
//     let end = Math.min(totalPages, start + maxVisible - 1);
    
//     if (end - start + 1 < maxVisible) {
//       start = Math.max(1, end - maxVisible + 1);
//     }
    
//     for (let i = start; i <= end; i++) {
//       pageNumbers.push(i);
//     }
    
//     return pageNumbers;
//   };

//   const getSideColor = (side) => {
//     return side === 1 ? "text-green-400" : "text-red-400";
//   };

//   if (!filteredLiveTradesData || filteredLiveTradesData.length === 0) {
//     return (
//       <div className="w-full h-full flex flex-col items-center justify-center text-zinc-400">
//         <div className="text-lg font-medium">No Trade History Found!!</div>
//       </div>
//     );
//   }

//   return (
//     <div className="w-full h-[calc(100vh-60px)] bg-zinc-900 flex flex-col">
//       {/* Sticky Header */}
//       <div className="bg-zinc-800 border-b border-zinc-700 sticky top-0 z-10 shrink-0">
//         <div className="grid grid-cols-6 gap-4 px-6 py-4 text-sm font-medium text-zinc-300">
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('symbol')}
//           >
//             <span>Symbol</span>
//             <ChevronsUpDown className="w-4 h-4" />
//           </button>
          
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('orderdatetime')}
//           >
//             <span>Date</span>
//             <ChevronsUpDown className="w-4 h-4" />
//           </button>
          
//           <div className="text-left">Side</div>
//           <div className="text-left">Quantity</div>
//           <div className="text-left">Traded Price</div>
          
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('strategy')}
//           >
//             <span>Strategy</span>
//             <ChevronsUpDown className="w-4 h-4" />
//           </button>
//         </div>
//       </div>

//       {/* Scrollable Table Body */}
//       <div className="flex-1 overflow-y-auto min-h-0">
//         <div className="min-w-full bg-zinc-900">
//           <div className="divide-y divide-zinc-700">
//             {currentItems.map((trade, index) => (
//               <div key={index} className="bg-zinc-900">
//                 {/* Main Trade Row */}
//                 <div className="grid grid-cols-6 gap-4 px-6 py-4 text-sm text-zinc-200 hover:bg-zinc-800 transition-colors">
//                   <div className="font-medium">
//                     {trade.symbol.replace("NSE:", "")}
//                   </div>
//                   <div>{FormatDateTime(trade.orderdatetime)}</div>
//                   <div className={`font-medium ${getSideColor(trade.side)}`}>
//                     {trade.side === 1 ? "BUY" : "SELL"}
//                   </div>
//                   <div>{trade.isIndexOption ? 
//                     trade.multiplierTiers?.reduce((sum, tier) => sum + (tier.clientsWithTrade.length * tier.quantity), 0) :
//                     trade.quantityTiers?.reduce((sum, tier) => sum + tier.quantity, 0)
//                   }</div>
//                   <div>₹{trade.tradeprice}</div>
//                   <div className="font-medium">{trade.strategy}</div>
//                 </div>

//                 {/* Enhanced Quantity-Client Sub Table */}
//                 <div className="px-6 pb-4">
//                   <div className="bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700">
//                     {trade.isIndexOption ? (
//                       // Index Options - ORIGINAL logic for BANKNIFTY, CA00246 logic for NIFTY
//                       <>
//                         <div className="bg-zinc-700 px-4 py-2">
//                           <div className="flex gap-4 text-xs font-medium text-zinc-300">
//                             <div className="min-w-[125px] flex items-start justify-start">Index Multiplier</div>
//                             <div className="min-w-[125px] flex items-start justify-start">
//                               {trade.symbol.toUpperCase().includes('BANKNIFTY') ? 'Quantity' : 'Expected Qty'}
//                             </div>
//                             <div className="min-w-[140px] flex items-start justify-start">Clients</div>
//                             {trade.baseQuantity && (
//                               <div className="min-w-[120px] flex items-start justify-start text-amber-400">
//                                 {trade.symbol.toUpperCase().includes('BANKNIFTY') 
//                                   ? `Base: ${trade.baseQuantity} (Fixed)`
//                                   : `Base: ${trade.baseQuantity} (CA00246)`
//                                 }
//                               </div>
//                             )}
//                           </div>
//                         </div>
                        
//                         <div className="divide-y divide-zinc-700">
//                           {trade.multiplierTiers.map((tier, tierIndex) => (
//                             <div key={tierIndex} className="grid grid-cols-[120px,120px,1fr] gap-4 px-4 py-3 text-sm items-start">
//                               <div className="font-medium text-blue-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
//                                 {tier.multiplier}
//                               </div>
//                               <div className="font-medium text-emerald-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
//                                 {tier.quantity}
//                               </div>
//                               <div className="flex flex-wrap gap-2">
//                                 {tier.allClients.map((user, userIndex) => {
//                                   const hasTrade = tier.clientsWithTrade.includes(user.client_id);
//                                   return (
//                                     <span 
//                                       key={userIndex}
//                                       className={`text-zinc-300 rounded px-2 py-1 text-xs flex items-center space-x-1 ${
//                                         hasTrade ? 'bg-green-800 border border-green-500/30' : 'bg-zinc-700'
//                                       }`}
//                                     >
//                                       {hasTrade && (
//                                         <Check className="w-3 h-3 text-white" />
//                                       )}
//                                       <span>{user.name}</span>
//                                     </span>
//                                   );
//                                 })}
//                               </div>
//                             </div>
//                           ))}
//                         </div>
//                       </>
//                     ) : (
//                       // Stock Options - Current Logic
//                       <>
//                         <div className="bg-zinc-700 px-4 py-2">
//                           <div className="grid grid-cols-2 gap-4 text-xs font-medium text-zinc-300">
//                             <div>Quantity</div>
//                             <div>Clients</div>
//                           </div>
//                         </div>
                        
//                         <div className="divide-y divide-zinc-700">
//                           {trade.quantityTiers.map((tier, tierIndex) => (
//                             <div key={tierIndex} className="grid grid-cols-[120px,1fr] gap-4 px-4 py-3 text-sm items-start">
//                               <div className="font-medium text-blue-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
//                                 {tier.quantity}
//                               </div>
//                               <div className="flex flex-wrap gap-2">
//                                 {accountNames
//                                   .filter(user => tier.clientIds.includes(user.client_id))
//                                   .map((user, userIndex) => {
//                                     const hasTrade = tier.clientIds.includes(user.client_id);
//                                     return (
//                                       <span 
//                                         key={userIndex}
//                                         className={`text-zinc-300 rounded px-2 py-1 text-xs flex items-center space-x-1 ${
//                                           hasTrade ? 'bg-green-900  border border-green-500/30' : 'bg-zinc-700'
//                                         }`}
//                                       >
//                                         {hasTrade && (
//                                           <Check className="w-3 h-3 text-white" />
//                                         )}
//                                         <span>{user.name}</span>
//                                       </span>
//                                     );
//                                   })
//                                 }
//                               </div>
//                             </div>
//                           ))}
//                         </div>
//                       </>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       </div>

//       {/* Fixed Pagination at Bottom */}
//       <div className="bg-zinc-800 border-t border-zinc-700 shrink-0 sticky bottom-0 z-10">
//         <div className="flex items-center justify-between px-6 py-4">
//           <div className="text-sm text-zinc-400">
//             Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, sortedTrades.length)} of {sortedTrades.length} trades
//           </div>
          
//           <div className="flex items-center space-x-2">
//             <button
//               onClick={() => handlePageChange(currentPage - 1)}
//               disabled={currentPage === 1}
//               className="flex items-center px-3 py-2 text-sm font-medium text-zinc-300 bg-zinc-700 rounded-lg hover:bg-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
//             >
//               <ChevronLeft className="w-4 h-4 mr-1" />
//               Previous
//             </button>
            
//             <div className="flex space-x-1">
//               {getPageNumbers().map(number => (
//                 <button
//                   key={number}
//                   onClick={() => handlePageChange(number)}
//                   className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
//                     currentPage === number
//                       ? 'bg-blue-600 text-white'
//                       : 'text-zinc-300 bg-zinc-700 hover:bg-zinc-600'
//                   }`}
//                 >
//                   {number}
//                 </button>
//               ))}
//             </div>
            
//             <button
//               onClick={() => handlePageChange(currentPage + 1)}
//               disabled={currentPage === totalPages}
//               className="flex items-center px-3 py-2 text-sm font-medium text-zinc-300 bg-zinc-700 rounded-lg hover:bg-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
//             >
//               Next
//               <ChevronRight className="w-4 h-4 ml-1" />
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }


